// MPC1000 / JJOS .ALL (all sequences & songs) parser — see FORMAT.md.
import { detectAllProfile } from './os-profile.js';

const DIR_OFFSET = 0x18;
const DIR_SLOT_SIZE = 24;
const DIR_SLOT_COUNT = 99;

const BAR_TABLE_OFFSET = 0x30;
// The bar table (1000 x 4-byte entries, from 0x30) ends at 0xfd0, but the
// real per-track records don't start until 0x1000 — the 48 bytes in between
// are a reserved/padding slot (see FORMAT.md).
const TRACK_TABLE_OFFSET = 0x1000;
const TRACK_RECORD_SIZE = 48;
const TRACK_COUNT = 64;
const EVENT_STREAM_OFFSET = 0x1c10;
const EVENT_RECORD_SIZE = 16;
const END_TICK = 0x7fffffff;
const ALL_VERSION_OFFSET = 0x04;
const ALL_VERSION_LENGTH = 16;
const FIXED_SONG_TABLE_OFFSET = 0x57e0;
const FIXED_SONG_SLOT_SIZE = 0x214;
const FIXED_SONG_SLOT_COUNT = 21;
const SONG_STEP_SEQUENCE_OFFSET = 0x14;
const SONG_STEP_REPEAT_OFFSET = 0x114;
const SONG_STEP_COUNT = 0x100;

function readAsciiZ(view, offset, length) {
  const bytes = new Uint8Array(view.buffer, view.byteOffset + offset, length);
  let end = bytes.indexOf(0);
  if (end === -1) end = length;
  return new TextDecoder('ascii').decode(bytes.subarray(0, end)).trim();
}

function assertBlobRange(buffer, entry, minLength) {
  const view = new DataView(buffer);
  if (entry.blobOffset + 4 > buffer.byteLength) {
    throw new Error(`Unsupported or corrupt .ALL file: "${entry.name}" points outside the file.`);
  }
  const selfLength = view.getUint32(entry.blobOffset, true);
  if (selfLength < minLength || entry.blobOffset + selfLength > buffer.byteLength) {
    throw new Error(`Unsupported or corrupt .ALL file: "${entry.name}" has an invalid data block.`);
  }
  return selfLength;
}

function parseTrackTable(buffer, blobStart, { profile = null } = {}) {
  const view = new DataView(buffer);
  const tracks = [];
  for (let t = 0; t < TRACK_COUNT; t++) {
    const off = blobStart + TRACK_TABLE_OFFSET + t * TRACK_RECORD_SIZE;
    const name = readAsciiZ(view, off, 16);
    // Byte +16 is a 1-byte flag/pad byte (observed 0x00) before the program
    // name text starts at +17 — reading from +16 truncates to "" immediately.
    const programName = readAsciiZ(view, off + 17, 15);
    // Fields below statistically confirmed across ~130 real projects
    // (~21K track records) — see FORMAT.md "Track table" for the survey.
    const rawSend = view.getUint8(off + 0x28);
    const rawLevel = view.getUint8(off + 42);
    const rawPan = view.getUint8(off + 47);
    // Stock Akai OS 1.x factory .ALL files carry valid track/program/event
    // data, but leave the JJOS-confirmed mixer bytes unset. Two real
    // patterns exist: the bare official factory preset, where Level/Pan/
    // Send are all 0 together (a totally untouched record - the original
    // target of this quirk, confirmed against "MPC1000 ALL 1.00"); and
    // real user-saved stock-OS projects (confirmed against two real
    // tutorial projects), where Level reads a genuine populated value
    // (100) but Pan/Send still read 0, because stock 1.x simply doesn't
    // have JJOS's live Track Mixer to ever write them. Requiring all
    // three zero (as this used to) missed the second pattern: a real
    // stock-OS file with a populated Level let its literal Pan=0 leak
    // through as a deliberate hard-left pan (heard as the whole track
    // playing dead left despite the file never having been panned by
    // anyone - see the "titenup"/"mpc1000-ebook-preview" bug report).
    // Pan/Send now infer independently of Level so a populated Level
    // doesn't block them from correctly falling back to center/off.
    // Level's own inference deliberately stays tied to the full
    // all-three-zero signature rather than going independent the same
    // way: unlike Pan/Send, a real stock-OS project *can* carry a
    // genuine, deliberate Level=0 (a track's fader pulled all the way
    // down - see the Level field note below), and nothing else in the
    // record proves that apart from Pan/Send also still being untouched.
    // Trusting Level=0 on its own would un-mute a track the user actually
    // silenced.
    const quirked = !!profile?.quirks?.inferAllZeroTrackMixerDefaults;
    const levelInferred = quirked && rawSend === 0 && rawLevel === 0 && rawPan === 0;
    const panInferred = quirked && rawPan === 0;
    const sendInferred = quirked && rawSend === 0;
    tracks.push({
      number: t + 1,
      name,
      programName,
      // 5 strongly correlates with "this is an audio track" (88% of
      // tracks whose name matches a real project WAV file carry this
      // value); MIDI tracks are overwhelmingly 0/1/2 instead. Matching the
      // track name against loaded sample buffers is more reliable, though
      // — this is kept mostly for reference/debugging.
      typeCode: view.getUint8(off + 35),
      // Confirmed against a real known-muted project (`NOV 18 LIVE`, an
      // 8-audio-track live layering session with exactly one take saved
      // muted) — see FORMAT.md's Track table section.
      muted: view.getUint8(off + 41) === 1,
      // 0-127ish, default/untouched value is 110; audio tracks show far
      // more variation (users pulling levels down when layering takes).
      level: levelInferred ? 100 : rawLevel,
      // 0-100, 50 = center — mirrors the PGM pad pan convention. Audio
      // tracks cluster hard at 0/50/100 (manual hard-panning of takes).
      pan: panInferred ? 50 : rawPan,
      // Track Mixer "Send" row — confirmed as a genuine continuous level
      // (0-100ish, default 30) via a histogram across ~35.6k real track
      // records, not yet acted on by playback (see FORMAT.md's open
      // questions — this is the level CC#91 automation would drive).
      send: sendInferred ? 30 : rawSend,
      mixerDefaultsInferred: levelInferred || panInferred || sendInferred,
    });
  }
  return tracks;
}

function parseEvents(buffer, blobStart, blobLength) {
  const view = new DataView(buffer);
  const events = [];
  let off = blobStart + EVENT_STREAM_OFFSET;
  const end = blobStart + blobLength;
  while (off + EVENT_RECORD_SIZE <= end) {
    const tick = view.getUint32(off, true);
    if (tick === END_TICK) break;
    events.push({
      tick,
      track: view.getUint8(off + 4),
      status: view.getUint8(off + 5),
      note: view.getUint8(off + 6),
      velocity: view.getUint8(off + 7),
      duration: view.getUint8(off + 8),
    });
    off += EVENT_RECORD_SIZE;
  }
  return events;
}

function parseSequenceBlob(buffer, blobStart, options = {}) {
  const view = new DataView(buffer);
  const selfLength = view.getUint32(blobStart + 0x00, true);
  const name = readAsciiZ(view, blobStart + 0x04, 16);
  const bars = view.getUint16(blobStart + 0x1c, true);
  const bpm = view.getUint16(blobStart + 0x20, true) / 10;

  return {
    name,
    bars,
    bpm,
    ticksPerBeat: 96,
    ticksPerBar: 384,
    tracks: parseTrackTable(buffer, blobStart, options),
    events: parseEvents(buffer, blobStart, selfLength),
    byteLength: selfLength,
  };
}

/**
 * A song's blob is NOT a sequence blob laid out differently — running it
 * through parseSequenceBlob would silently misread its bytes as bars/bpm/
 * track-table/event-stream and produce garbage. What's confirmed so far
 * (via real project data — see FORMAT.md): self-length (u32) + 16-byte
 * name, matching the sequence blob's own header convention exactly, then
 * a repeating body that clearly encodes the song's step list (which
 * sequence, in what order, how many times) but whose exact field layout
 * isn't reliably decoded yet — the byte offsets that looked like a clean
 * repeating record in one example didn't hold up across others. Exposing
 * only what's verified (name, byte length) rather than guessing avoids
 * shipping a step order that might be silently wrong.
 */
function parseSongBlob(buffer, blobStart) {
  const view = new DataView(buffer);
  const selfLength = view.getUint32(blobStart + 0x00, true);
  const name = readAsciiZ(view, blobStart + 0x04, 16);
  return { kind: 'song', name, byteLength: selfLength, blobOffset: blobStart, steps: [], sequence: null };
}

function parseFixedSongSlot(buffer, slotOffset, slotIndex, sequences) {
  const view = new DataView(buffer);
  const name = readAsciiZ(view, slotOffset, 16);
  if (!/^song/i.test(name)) return null;

  const steps = [];
  for (let i = 0; i < SONG_STEP_COUNT; i++) {
    const sequenceOrdinal = view.getUint8(slotOffset + SONG_STEP_SEQUENCE_OFFSET + i);
    if (!sequenceOrdinal) break;
    const repeats = Math.max(1, view.getUint8(slotOffset + SONG_STEP_REPEAT_OFFSET + i) || 1);
    steps.push({
      index: i,
      sequenceOrdinal,
      repeats,
      sequence: sequences[sequenceOrdinal - 1] ?? null,
    });
  }

  // Empty default song slots are named "Song" too. Ignore them unless they
  // actually point at sequence steps.
  if (!steps.length) return null;

  const song = {
    kind: 'song',
    name,
    byteLength: FIXED_SONG_SLOT_SIZE,
    blobOffset: slotOffset,
    slot: slotIndex,
    steps,
    sequence: null,
  };
  song.sequence = buildSongSequence(song);
  return song;
}

function cloneTrackForSong(track, number) {
  return {
    ...track,
    number,
    sourceTrackNumber: track.number,
  };
}

function songTrackKey(track) {
  return [
    track.number,
    track.name,
    track.programName,
    track.typeCode,
    track.muted ? 1 : 0,
    track.level,
    track.pan,
    track.send,
  ].join('\0');
}

function buildSongSequence(song) {
  const firstSequence = song.steps.find((step) => step.sequence)?.sequence;
  if (!firstSequence) return null;

  const tracks = [];
  const trackNumberByKey = new Map();
  const events = [];
  let tickOffset = 0;
  let bars = 0;
  let hasTempoMismatch = false;
  let skippedMissingSequences = 0;

  const addTrack = (track) => {
    const key = songTrackKey(track);
    let number = trackNumberByKey.get(key);
    if (!number) {
      number = tracks.length + 1;
      trackNumberByKey.set(key, number);
      tracks.push(cloneTrackForSong(track, number));
    }
    return number;
  };

  for (const step of song.steps) {
    const sequence = step.sequence;
    if (!sequence) {
      skippedMissingSequences += step.repeats;
      continue;
    }
    if (sequence.bpm !== firstSequence.bpm || sequence.ticksPerBeat !== firstSequence.ticksPerBeat || sequence.ticksPerBar !== firstSequence.ticksPerBar) {
      hasTempoMismatch = true;
    }
    const sequenceTicks = sequence.bars * sequence.ticksPerBar;
    for (let repeat = 0; repeat < step.repeats; repeat++) {
      for (const event of sequence.events) {
        const sourceTrack = sequence.tracks[event.track - 1];
        if (!sourceTrack) continue;
        events.push({
          ...event,
          tick: event.tick + tickOffset,
          track: addTrack(sourceTrack),
          sourceSequenceOrdinal: step.sequenceOrdinal,
          sourceSequenceName: sequence.name,
          sourceTrackNumber: sourceTrack.number,
        });
      }
      tickOffset += sequenceTicks;
      bars += sequence.bars;
    }
  }

  return {
    kind: 'song-sequence',
    name: song.name,
    songName: song.name,
    songSteps: song.steps,
    songBlobOffset: song.blobOffset,
    hasTempoMismatch,
    skippedMissingSequences,
    bars,
    bpm: firstSequence.bpm,
    ticksPerBeat: firstSequence.ticksPerBeat,
    ticksPerBar: firstSequence.ticksPerBar,
    tracks,
    events: events.sort((a, b) => a.tick - b.tick),
    byteLength: 0,
    slot: song.slot,
    blobOffset: song.blobOffset,
  };
}

function parseFixedSongTable(buffer, sequences, firstBlobOffset) {
  if (buffer.byteLength < FIXED_SONG_TABLE_OFFSET + FIXED_SONG_SLOT_SIZE) return [];
  if (firstBlobOffset <= FIXED_SONG_TABLE_OFFSET) return [];

  const tableEnd = Math.min(
    firstBlobOffset,
    FIXED_SONG_TABLE_OFFSET + FIXED_SONG_SLOT_COUNT * FIXED_SONG_SLOT_SIZE
  );
  const songs = [];
  for (
    let slotOffset = FIXED_SONG_TABLE_OFFSET, slotIndex = 0;
    slotOffset + FIXED_SONG_SLOT_SIZE <= tableEnd;
    slotOffset += FIXED_SONG_SLOT_SIZE, slotIndex++
  ) {
    const song = parseFixedSongSlot(buffer, slotOffset, slotIndex, sequences);
    if (song) songs.push(song);
  }
  return songs;
}

export function parseALL(arrayBuffer) {
  if (arrayBuffer.byteLength < DIR_OFFSET) {
    throw new Error('Unsupported or corrupt .ALL file: header is too short.');
  }
  const view = new DataView(arrayBuffer);
  const containerHeader = readAsciiZ(view, 0x00, 16);
  if (/^MPC2KXL ALL\b/.test(containerHeader)) {
    throw new Error('MPC2000XL .ALL files are not supported yet. This player currently supports MPC1000/JJOS .ALL projects.');
  }
  // u32, not u16 — confirmed by scanning 131 real projects: any .ALL file
  // over 64KB (most non-trivial ones) showed this field wrapping mod 65536
  // when read as u16. Doesn't affect parsing correctness (nothing else uses
  // this field for offset math — each sequence's own blob self-length,
  // read separately below, is what matters), but it was wrong.
  const fileSize = view.getUint32(0x00, true);
  const version = readAsciiZ(view, ALL_VERSION_OFFSET, ALL_VERSION_LENGTH);
  if (!/^MPC1000 ALL\b/.test(version)) {
    const label = containerHeader || version || 'unknown';
    throw new Error(`Unsupported .ALL header "${label}". This player currently supports MPC1000/JJOS .ALL projects.`);
  }
  const profile = detectAllProfile(version);

  const dirEntries = [];
  for (let i = 0; i < DIR_SLOT_COUNT; i++) {
    const off = DIR_OFFSET + i * DIR_SLOT_SIZE;
    const name = readAsciiZ(view, off, 16);
    const blobOffset = view.getUint32(off + 20, true);
    if (name && name !== '(unused)') {
      dirEntries.push({ slot: i, name, blobOffset });
    }
  }

  // Songs live in the *same* 99-slot directory as sequences, distinguished
  // only by name convention ("Song01" vs "SequenceNN" — confirmed against
  // 12 real song entries across 5 real projects). A song's blob is a
  // different structure entirely (see parseSongBlob) — routing it through
  // parseSequenceBlob would misread its bytes as a broken sequence with
  // garbage bars/bpm and let it show up as something playable that isn't.
  const songEntries = dirEntries.filter((entry) => /^song\d*$/i.test(entry.name));
  const sequenceEntries = dirEntries.filter((entry) => !/^song\d*$/i.test(entry.name));
  for (const entry of sequenceEntries) {
    assertBlobRange(arrayBuffer, entry, EVENT_STREAM_OFFSET);
  }
  for (const entry of songEntries) {
    assertBlobRange(arrayBuffer, entry, 20);
  }

  // Sequence *names* are not unique — duplicating a sequence on the
  // hardware without renaming it produces multiple directory entries with
  // the same name but distinct offsets (see FORMAT.md). `blobOffset` is
  // each entry's own resolved file offset, always unique, and is what the
  // rest of the app uses as a sequence's real identity — `.name` is
  // display-only.
  const sequences = sequenceEntries.map((entry) => ({
    ...parseSequenceBlob(arrayBuffer, entry.blobOffset, { profile }),
    slot: entry.slot,
    blobOffset: entry.blobOffset,
  }));

  const directorySongs = songEntries.map((entry) => ({
    ...parseSongBlob(arrayBuffer, entry.blobOffset),
    slot: entry.slot,
    blobOffset: entry.blobOffset,
  }));
  const firstBlobOffset = Math.min(...sequenceEntries.map((entry) => entry.blobOffset));
  const fixedSongs = parseFixedSongTable(arrayBuffer, sequences, firstBlobOffset);
  const songs = [...directorySongs, ...fixedSongs];

  return { kind: 'all', fileSize, version, profile, sequences, songs };
}
